import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Geolocation } from '@capacitor/geolocation';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { environment } from '../../../environments/environment';
import { AuthToken, AuthenticationResponse, LogoutResponse, PsRegistrationData } from '../models/auth-token.model';
import { authTokenToModel, decodeUserFromJwt, forgetPasswordToModel, registerToPayload, registerUserToModel } from '../api/adapters';
import { storagePrefix } from '../utils/storage-key';
import { SentryService } from './sentry.service';
import {
  IdbStorageService, IDB_STORES_CLEARED_ON_LOGOUT, IDB_STORE_QUEUE, IDB_STORE_SIGNATURE,
} from './idb-storage.service';

/**
 * Unscoped bootstrap key — the only auth key stored without a server entity ID.
 * Read first on startup to decode the JWT and derive the entityId, which then
 * constructs all other scoped storage key names.
 */
const ACCESS_TOKEN_KEY = 'auth_access_token';

/** Base path for all auth endpoints. */
const AUTH_BASE = `${environment.apiUrl}/auth`;

/**
 * JS-level geolocation safety timeout (ms). Slightly longer than the native
 * `timeout` hint passed to `getCurrentPosition` so that if the iOS
 * `CLLocationManager` RunLoop hang causes the native timeout to silently fail,
 * `Promise.race` still rejects and we fall back to `"0"/"0"`.
 */
const GEO_JS_TIMEOUT_MS = 6000;

/**
 * localStorage key that persists the just-expired user's email + entity ID
 * across page reloads so the password-only re-login UI survives an F5.
 * Listed in {@link PRESERVED_ON_LOGOUT} so it is not swept by the logout
 * localStorage pass. Cleared on next successful {@link AuthService.login}.
 */
const REAUTH_CONTEXT_KEY = 'session_expired_context';

/**
 * Key fragments that survive the logout localStorage sweep: the shared app
 * settings cache, unsent work (offline submission queue, its pre-auth
 * fallback, pending event logs) that must upload on the user's next login,
 * and the saved e-signature (legacy web fallback copy).
 * Matched with `includes()` so the `cap_sec_` web-fallback prefix of
 * `capacitor-secure-storage-plugin` is covered.
 */
const PRESERVED_ON_LOGOUT = [
  'app_settings_cache',
  '_queue_submissions',
  'upqueue_pending',
  '_pending_event_logs',
  '_esign_data',
  REAUTH_CONTEXT_KEY,
] as const;

/**
 * Whether a localStorage key must survive {@link AuthService} logout.
 *
 * @param key - Raw localStorage key.
 * @returns `true` for the shared settings cache and unsent-work keys.
 */
export function isPreservedOnLogout(key: string): boolean {
  return PRESERVED_ON_LOGOUT.some(fragment => key.includes(fragment));
}

/**
 * Whether a SecureStorage key holds a non-empty JSON array.
 *
 * @param key - SecureStorage key.
 * @returns `false` when the key is absent, unreadable or an empty array.
 */
async function secureArrayHasItems(key: string): Promise<boolean> {
  try {
    const { value } = await SecureStoragePlugin.get({ key });
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

/**
 * Returns the three scoped auth key names for the given process server entity ID.
 * Keys are prefixed with an opaque FNV-1a hash so the raw ID is never visible
 * in the stored key name (e.g. `cap_sec_3_auth_refresh_token`).
 *
 * @param entityId - Process server entity ID from the JWT (`claim.entity_id`).
 */
function scopedAuthKeys(entityId: number) {
  const pfx = storagePrefix(entityId);
  return {
    REFRESH_TOKEN: `${pfx}_auth_refresh_token`,
    EXPIRES_AT:    `${pfx}_auth_expires_at`,
    USERNAME:      `${pfx}_auth_username`,
  } as const;
}

/**
 * Manages JWT-based authentication for the Titanium Legal process server app.
 *
 * @remarks
 * Uses Angular Signals for reactive auth state. The current token is persisted
 * in secure storage (Keychain on iOS, Keystore on Android) so sessions survive app restarts.
 *
 * **Storage key policy:**
 * - `auth_access_token` — stored **without** server ID (bootstrap key, read first on startup).
 * - `{hashedEntityId}_auth_refresh_token`, `_auth_expires_at`, `_auth_username` — scoped.
 *
 * **Login flow** — `POST /auth/token`
 * - Credentials are sent as `Authorization: Basic <base64>` (HTTP Basic Auth).
 * - The form body carries only `role=ps`.
 * - Five device-context headers are required per the API contract.
 *
 * **Refresh flow** — `POST /auth/refresh`
 * - The current `accessToken` is sent as a Bearer token.
 * - No separate refresh-token field exists in the API.
 *
 * **Logout flow** — `POST /auth/logout`
 * - Invalidates the token server-side then clears local state.
 *
 * @example
 * ```ts
 * const auth = inject(AuthService);
 * if (auth.isAuthenticated()) { ... }
 * await auth.login('user@example.com', 'secret');
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _token        = signal<AuthToken | null>(null);
  private readonly http          = inject(HttpClient);
  private readonly sentryService = inject(SentryService);
  private readonly idb           = inject(IdbStorageService);

  /**
   * Read-only signal containing the current {@link AuthToken}, or `null`
   * when the user is not authenticated.
   */
  readonly token = this._token.asReadonly();

  private readonly _sessionExpired$ = new Subject<void>();

  /**
   * Emits once whenever the session is forcibly terminated by
   * {@link clearSession} — i.e., when the refresh token is also expired.
   * `LoginComponent` subscribes to this to show the "Session expired" toast
   * after the login screen has finished rendering.
   */
  readonly sessionExpired$: Observable<void> = this._sessionExpired$.asObservable();

  /**
   * One-shot flag set when the session ends involuntarily — see
   * {@link consumeSessionExpiredNotice}.
   */
  private sessionExpiredNotice = false;

  /**
   * Email of the user whose session just expired.
   *
   * @remarks
   * Captured in {@link clearSession} before storage is wiped, so
   * `LoginComponent` can pre-fill the username field after an involuntary
   * session end (password-only re-login flow). Cleared on next {@link login}.
   */
  private _lastKnownUsername: string | null = null;

  /**
   * Entity ID of the user whose session just expired.
   *
   * @remarks
   * Stored alongside {@link _lastKnownUsername} so that
   * {@link checkHasUnsentWork} can scope its storage lookup to the correct
   * user without needing a live token. Cleared on next {@link login}.
   */
  private _lastKnownEntityId: number | null = null;

  /**
   * Returns `true` exactly once after an involuntary session end
   * ({@link clearSession}, or a failed silent refresh at app start), then
   * resets.
   *
   * @remarks
   * {@link sessionExpired$} is a plain Subject: it emits before the login
   * screen has been created (the interceptor clears the session BEFORE
   * navigating, so `guestGuard` doesn't bounce back to `/dashboard`), so a
   * subscriber created afterwards would miss it. `LoginComponent` reads this
   * on init instead, guaranteeing the "Session expired" toast is shown once.
   *
   * @returns Whether the "Session expired" toast should be shown now.
   */
  consumeSessionExpiredNotice(): boolean {
    const pending = this.sessionExpiredNotice;
    this.sessionExpiredNotice = false;
    return pending;
  }

  /**
   * Email of the user whose session was most recently cleared involuntarily,
   * or `null` when the user logged out manually or no session has expired yet.
   *
   * @remarks
   * Read by `LoginComponent.ngOnInit` to decide whether to enter the
   * password-only re-login mode (username pre-filled, read-only).
   */
  get lastKnownUsername(): string | null {
    return this._lastKnownUsername;
  }

  /**
   * Returns `true` when the user whose session just expired still has
   * submissions or event logs waiting to upload.
   *
   * @remarks
   * Uses the entity ID captured at session-expiry time so the lookup is
   * correctly scoped even though the token is now null. Returns `false`
   * when no expired-session context is available.
   *
   * Called by `LoginComponent` to decide whether "Not you?" should be
   * blocked (the user must upload first before switching accounts).
   */
  async checkHasUnsentWork(): Promise<boolean> {
    if (this._lastKnownEntityId === null) return false;
    return this.hasUnsentWork(storagePrefix(this._lastKnownEntityId));
  }

  private readonly _manualLogoutInProgress = signal(false);

  /**
   * `true` from the moment {@link logout} is called until the next
   * successful {@link login}.
   *
   * @remarks
   * **2026-07-29 fix** — lets `authInterceptor` distinguish a genuine
   * involuntary session expiration from a stray 401 on a request that
   * happened to be in flight when the user deliberately logged out.
   * `logout()`'s own doc comment already notes the server-side `POST
   * /auth/logout` call invalidates the token remotely — so any *other*
   * request already in flight at that moment (a background `autoSync()`
   * tick, a queued `EventLogService` flush, etc.) using the same
   * now-invalidated Bearer token can come back with a 401 shortly after.
   * `authInterceptor`'s `refreshAndRetry()` previously had no way to tell
   * that apart from a real expired-session 401 — its `!auth.token()` check
   * is `true` immediately after `logout()` clears the token either way — so
   * it called `clearSession()` (emitting {@link sessionExpired$}) on top of
   * an already-in-progress, perfectly normal logout. The officer saw
   * "Session expired. Please sign in again." immediately after tapping
   * "Log out" themselves. Reset to `false` on the next successful `login()`
   * so a real subsequent session-expiry event is still handled normally.
   */
  readonly manualLogoutInProgress = this._manualLogoutInProgress.asReadonly();

  /**
   * Computed signal that is `true` when a non-expired token is present.
   *
   * @remarks
   * Compares `Date.now()` against {@link AuthToken.expiresAt} so the value
   * becomes `false` automatically once the token window closes.
   */
  readonly isAuthenticated = computed(() => {
    const t = this._token();
    return t !== null && Date.now() < t.expiresAt;
  });

  /**
   * Restores the persisted token and returns when complete.
   * Called by the `APP_INITIALIZER` so that the auth guard can see the
   * correct `isAuthenticated()` state before the first route is resolved.
   *
   * @remarks
   * Replacing the fire-and-forget constructor call eliminates the race
   * condition where the guard runs before the async token restoration
   * finishes, causing an incorrect redirect to `/auth`.
   */
  async initialize(): Promise<void> {
    await this.restoreStoredToken();
  }

  /**
   * Resolves once the startup token-restore sequence has finished.
   *
   * @remarks
   * `authGuard` awaits this before checking {@link isAuthenticated} so the
   * guard never fires before the stored session has been read from secure
   * storage — preventing the spurious logout-on-reload race condition.
   *
   * Awaiting an already-resolved Promise is instantaneous, so subsequent
   * guard checks after the first navigation have zero overhead.
   */
  readonly initialized: Promise<void>;

  constructor() {
    this.initialized = this.restoreStoredToken();
  }

  /**
   * Authenticates the process server and stores the returned JWT.
   *
   * @remarks
   * Sends credentials as `Authorization: Basic <base64(username:password)>`
   * with form body `role=ps` to `POST /auth/token`.
   * Device-context headers are collected from Capacitor APIs before the request.
   * On success the token is persisted in secure storage and the
   * {@link token} signal is updated.
   *
   * @param username - Process server login username.
   * @param password - Login password (discarded after the response is received).
   * @throws When the API returns a non-2xx status or the network is unavailable.
   */
  async login(username: string, password: string): Promise<void> {
    const headers = await this.buildLoginHeaders(username, password);
    const body = new HttpParams().set('role', 'ps');

    const response = await firstValueFrom(
      this.http.post<AuthenticationResponse>(`${AUTH_BASE}/token`, body.toString(), { headers }),
    );

    const token = authTokenToModel(response);
    await this.persistToken(token);
    this._token.set(token);
    this._manualLogoutInProgress.set(false);
    this.clearReauthContext();
    // Immediately exchange for a long-lived token — authInterceptor attaches
    // the Bearer header automatically for this follow-up call.
    await this.refresh();
  }

  /**
   * Exchanges the current access token for a fresh one via
   * `POST /auth/refresh`.
   *
   * @remarks
   * The `authInterceptor` attaches `Authorization: Bearer` automatically.
   * Called automatically at the end of {@link login} and can be called
   * manually to extend the session before the token expires.
   * Updates {@link token} and secure storage on success.
   *
   * @throws When there is no current token, or the API returns a non-2xx status.
   */
  async refresh(): Promise<void> {
    const current = this._token();
    if (!current) throw new Error('AuthService.refresh: no active token to refresh.');

    const response = await firstValueFrom(
      this.http.post<AuthenticationResponse>(`${AUTH_BASE}/refresh`, null),
    );

    const token = authTokenToModel(response);
    await this.persistToken(token);
    this._token.set(token);
    this.sentryService.setUser({ id: String(token.user.entityId), email: token.user.email, username: token.user.name });
  }

  /**
   * Signs the user out and clears all local auth state.
   *
   * @remarks
   * Per spec §7.6 — "fire-and-forget: do not block on response".
   * The HTTP call is intentionally NOT awaited: we fire it and immediately
   * clear local state so the user is signed out without waiting for the server.
   * Local state is cleared even if the API call later fails.
   * The `authInterceptor` attaches `Authorization: Bearer` automatically.
   */
  async logout(): Promise<void> {
    this._manualLogoutInProgress.set(true);
    const current = this._token();

    // Fire-and-forget: do not await or block on the server response.
    // Token is still set in the signal when subscribe() is called so the
    // interceptor can read it before _token.set(null) clears it.
    if (current) {
      this.http
        .post<LogoutResponse>(`${AUTH_BASE}/logout`, null)
        .subscribe({ error: () => void 0 });
    }

    await this.clearAllStorage();
    this._token.set(null);
    this.sentryService.setUser(null);
  }

  /**
   * Clears local auth state without calling the logout API.
   *
   * @remarks
   * Used by {@link authInterceptor} when a session has definitively expired
   * (i.e., after `MAX_REFRESH_ATTEMPTS` consecutive 401 failures). Unlike
   * {@link logout}, this method does not fire a server-side logout request —
   * the token is already invalid so there is nothing to invalidate remotely.
   */
  async clearSession(): Promise<void> {
    // Capture before storage is wiped so LoginComponent can pre-fill the field
    // and block "Not you?" while unsent work exists.
    this._lastKnownUsername = this._token()?.user?.email ?? null;
    this._lastKnownEntityId = this._token()?.user?.entityId ?? null;
    // Persist to localStorage so the reauth UI survives a page reload.
    this.persistReauthContext();
    await this.clearAllStorage();
    this._token.set(null);
    this.sentryService.setUser(null);
    this.sessionExpiredNotice = true;
    this._sessionExpired$.next();
  }

  /**
   * Registers a new process-server account via `POST /auth/register`.
   *
   * @remarks
   * Sends the form data as `multipart/form-data` (required by the API).
   * On success the API returns a confirmation message; the user must then
   * verify their email before they can log in.
   *
   * @param data - Registration form values in camelCase domain shape.
   * @returns The server confirmation message to display to the user.
   */
  async register(data: PsRegistrationData): Promise<string> {
    const payload = registerToPayload(data);
    const formData = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      if (value !== undefined) {
        formData.append(key, String(value));
      }
    }
    const raw = await firstValueFrom(
      this.http.post<unknown>(`${AUTH_BASE}/register`, formData),
    );
    return registerUserToModel(raw).message;
  }

  /**
   * Requests a password-reset link from `GET /auth/forgetpassword`.
   *
   * @remarks
   * The API always responds with a success envelope regardless of whether an
   * account exists, preventing user enumeration.
   *
   * @param email - The registered email address of the process server.
   * @returns The server response message to display to the user.
   */
  async forgotPassword(email: string): Promise<string> {
    const raw = await firstValueFrom(
      this.http.get<unknown>(
        `${AUTH_BASE}/forgetpassword`,
        { params: new HttpParams().set('email', email) },
      ),
    );
    return forgetPasswordToModel(raw).message;
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  /**
   * Restores a persisted auth session from secure storage on app start.
   *
   * @remarks
   * Bootstrap sequence:
   * 1. Read the unscoped `auth_access_token` key.
   * 2. Decode the JWT to obtain the server entity ID.
   * 3. Read the scoped `{prefix}_auth_expires_at` key using that entity ID.
   * 4. Restore the signal or attempt a silent refresh if expired.
   *
   * Three startup scenarios per the auth spec (§7.7):
   * - **Valid token**: restore signal directly — no network call.
   * - **Expired token + refresh key present**: attempt a silent `POST /auth/refresh`.
   * - **No token / missing keys**: return — `authGuard` handles redirect.
   *
   * Also runs one-time migration to remove old unscoped keys written by
   * previous builds before server-ID scoping was introduced.
   */
  private async restoreStoredToken(): Promise<void> {
    // Restore session-expired username/entityId from previous page load so the
    // password-only re-login UI shows even after a browser refresh.
    this.restoreReauthContext();

    // Migration: remove legacy unscoped keys from builds prior to server-ID scoping.
    await Promise.allSettled([
      'auth_token',
      'auth_refresh_token',
      'auth_expires_at',
      'auth_username',
    ].map(key => SecureStoragePlugin.remove({ key })));

    let accessToken: string;
    try {
      accessToken = (await SecureStoragePlugin.get({ key: ACCESS_TOKEN_KEY })).value;
    } catch {
      return;
    }

    const user = decodeUserFromJwt(accessToken);
    if (!user?.entityId) return;

    const scoped = scopedAuthKeys(user.entityId);
    let expiresAt: number;
    try {
      expiresAt = parseInt((await SecureStoragePlugin.get({ key: scoped.EXPIRES_AT })).value, 10);
    } catch {
      return;
    }

    const token = this.buildRestoredToken(accessToken, expiresAt);

    if (Date.now() < expiresAt) {
      // Valid token — no session expiry; discard any stale reauth context.
      this.clearReauthContext();
      this._token.set(token);
      this.sentryService.setUser({ id: String(token.user.entityId), email: token.user.email, username: token.user.name });
      return;
    }

    await this.attemptStartupRefresh(token);
  }

  /**
   * Constructs an {@link AuthToken} from individual stored fields.
   * Called during restore when a full API response is not available.
   *
   * @param accessToken - Stored JWT string.
   * @param expiresAt   - Stored expiry timestamp in milliseconds.
   */
  private buildRestoredToken(accessToken: string, expiresAt: number): AuthToken {
    return {
      accessToken,
      tokenType: 'bearer',
      expiresIn: Math.max(0, Math.round((expiresAt - Date.now()) / 1000)),
      expiresAt,
      user: decodeUserFromJwt(accessToken),
    };
  }

  /**
   * Attempts a silent token refresh during startup when the stored token is expired.
   * Sets the expired token on the signal temporarily so the interceptor can
   * attach a Bearer header to the refresh request.
   *
   * @param expiredToken - Reconstructed (expired) token to use as the refresh credential.
   */
  private async attemptStartupRefresh(expiredToken: AuthToken): Promise<void> {
    const scoped = scopedAuthKeys(expiredToken.user.entityId);
    try {
      await SecureStoragePlugin.get({ key: scoped.REFRESH_TOKEN });
    } catch {
      return;
    }
    this._token.set(expiredToken);
    try {
      await this.refresh();
      // Refresh succeeded — session restored; discard any stale reauth context.
      this.clearReauthContext();
    } catch {
      this._lastKnownUsername = expiredToken.user?.email ?? null;
      this._lastKnownEntityId = expiredToken.user?.entityId ?? null;
      // Persist so the password-only re-login UI survives a page reload.
      this.persistReauthContext();
      await this.clearAllStorage();
      this._token.set(null);
      this.sessionExpiredNotice = true;
    }
  }

  /**
   * Persists all auth keys to secure storage.
   *
   * `auth_access_token` is stored without a server ID (bootstrap key).
   * The remaining three keys are scoped to `{hashedEntityId}_*` so different
   * users on the same device never share or expose each other's session data.
   *
   * @param token - The token to persist.
   */
  private async persistToken(token: AuthToken): Promise<void> {
    const scoped = scopedAuthKeys(token.user.entityId);
    await SecureStoragePlugin.set({ key: ACCESS_TOKEN_KEY,      value: token.accessToken });
    await SecureStoragePlugin.set({ key: scoped.REFRESH_TOKEN,  value: token.accessToken });
    await SecureStoragePlugin.set({ key: scoped.EXPIRES_AT,     value: String(token.expiresAt) });
    await SecureStoragePlugin.set({ key: scoped.USERNAME,       value: token.user.name });
  }

  /**
   * Clears user data from storage on logout (SecureStoragePlugin, localStorage
   * and IndexedDB) — EXCEPT unsent work waiting to be uploaded.
   *
   * @remarks
   * **Cleared:** auth tokens, username, job cache, reference data,
   * in-progress evidence photos, and every other localStorage key.
   *
   * **Saved e-signature — cleared only when nothing is pending (2026-10-09):**
   * `{pfx}_esign_data` (Keychain / Keystore on native; AES-GCM–encrypted
   * `IDB_STORE_SIGNATURE` on web) is removed when the user has no unsent
   * submissions or event logs (see {@link hasUnsentWork}). A manual logout is
   * only possible in that state, so it always clears the signature; an
   * involuntary logout (session expiry) with unsent work keeps it until the
   * same user signs in again.
   *
   * **Kept (2026-10-09, product direction):** a manual logout is only offered
   * once nothing is pending (the "Log out anyway" option was removed), but
   * involuntary logouts — {@link clearSession} on session expiry, a failed
   * startup refresh, an in-flight upload racing the logout — can still happen
   * with unsent work. That work must never be destroyed, so everything
   * "Transmit all" on `/upqueue` sends survives and uploads automatically when
   * the SAME user signs in again:
   * - Offline submission queue — attempts (`/jobs/upqueue`), accepts
   *   (`/jobs/acceptqueue`), affidavits (`/jobs/esignqueue`): the whole
   *   `IDB_STORE_QUEUE` store, plus `{pfx}_queue_submissions` /
   *   `upqueue_pending` in SecureStorage (legacy / IndexedDB-unavailable
   *   fallback).
   * - Pending event logs (`/eventlog/add`): `{pfx}_pending_event_logs`.
   * - `IDB_STORE_CRYPTO_KEYS` — the device key that decrypts queued affidavit
   *   documents; dropping it would make them unsendable.
   *
   * All are keyed by the hashed entity ID, so a different officer signing in
   * on the same device never loads them. See {@link isPreservedOnLogout}.
   */
  private async clearAllStorage(): Promise<void> {
    // SecureStoragePlugin's web fallback lives in localStorage (cap_sec_ prefix),
    // so the sweep below must skip the preserved queue keys too.
    const allKeys = Object.keys(localStorage);
    const userKeys = allKeys.filter(key => !isPreservedOnLogout(key));

    const entityId = this._token()?.user?.entityId;
    if (entityId) {
      await this.clearUserScopedKeys(entityId);
    }

    // Clear localStorage except app settings and the preserved unsent-work keys.
    for (const key of userKeys) {
      try { localStorage.removeItem(key); } catch { /* unavailable */ }
    }

    // Clear the per-user IndexedDB caches. IDB_STORES_CLEARED_ON_LOGOUT
    // excludes the offline queue and the device crypto key (see
    // IdbStorageService) so unsent submissions survive logout.
    await Promise.allSettled(IDB_STORES_CLEARED_ON_LOGOUT.map(store => this.idb.clearStore(store)));
  }

  /**
   * Removes the user-scoped SecureStorage keys cleared on logout, plus the
   * saved e-signature when the user has no unsent work.
   *
   * @remarks
   * SecureStoragePlugin can't be enumerated, so known key patterns are removed.
   * `{pfx}_queue_submissions` and `{pfx}_pending_event_logs` are never removed
   * here — see {@link clearAllStorage}.
   *
   * @param entityId - Entity ID of the user being logged out.
   */
  private async clearUserScopedKeys(entityId: number): Promise<void> {
    const pfx = storagePrefix(entityId);
    const scoped = scopedAuthKeys(entityId);
    const clearSignature = !(await this.hasUnsentWork(pfx));
    await Promise.allSettled([
      SecureStoragePlugin.remove({ key: ACCESS_TOKEN_KEY }),
      SecureStoragePlugin.remove({ key: scoped.REFRESH_TOKEN }),
      SecureStoragePlugin.remove({ key: scoped.EXPIRES_AT }),
      SecureStoragePlugin.remove({ key: scoped.USERNAME }),
      SecureStoragePlugin.remove({ key: `${pfx}_cache_jobs` }),
      SecureStoragePlugin.remove({ key: `${pfx}_cache_jobs_at` }),
      SecureStoragePlugin.remove({ key: `${pfx}_ref_data_cache` }),
      // IndexedDB copy (web) + SecureStorage copy (native / legacy web).
      ...(clearSignature ? [this.idb.removeEverywhere(IDB_STORE_SIGNATURE, `${pfx}_esign_data`)] : []),
    ]);
  }

  /**
   * Whether the user still has submissions or event logs waiting to upload.
   *
   * @remarks
   * Reads storage directly (not `UpqueueService` / `EventLogService`, which
   * both inject this service). Checks every storage location that may hold
   * the user's pending work:
   *
   * - `{pfx}_queue_submissions` in IDB (primary, written by `writeQueue`).
   * - `{pfx}_queue_submissions` in SecureStorage (fallback when IDB is
   *   unavailable or its write failed).
   * - `upqueue_pending` in IDB + SecureStorage — the pre-auth staging key
   *   used when submissions arrive before `restoreQueue()` runs on login.
   *   Preserved on logout by {@link isPreservedOnLogout}; normally migrated
   *   to the scoped key by `UpqueueService.restoreQueue()`, but may still
   *   hold items if the session expired before that ran.
   * - `{pfx}_pending_event_logs` in SecureStorage (event logs).
   *
   * @param pfx - The user's {@link storagePrefix}.
   * @returns `true` when any location holds at least one item.
   */
  private async hasUnsentWork(pfx: string): Promise<boolean> {
    const queueKey    = `${pfx}_queue_submissions`;
    const preAuthKey  = 'upqueue_pending';
    const [idbQueue, fallbackQueue, idbPreAuth, securePreAuth, eventLogs] = await Promise.all([
      this.idb.get<unknown>(IDB_STORE_QUEUE, queueKey).catch(() => null),
      secureArrayHasItems(queueKey),
      this.idb.get<unknown>(IDB_STORE_QUEUE, preAuthKey).catch(() => null),
      secureArrayHasItems(preAuthKey),
      secureArrayHasItems(`${pfx}_pending_event_logs`),
    ]);
    const hasScoped  = Array.isArray(idbQueue)   && idbQueue.length   > 0;
    const hasPreAuth = Array.isArray(idbPreAuth) && idbPreAuth.length > 0;
    return hasScoped || fallbackQueue || hasPreAuth || securePreAuth || eventLogs;
  }

  /**
   * Writes the expired user's email and entity ID to localStorage so the
   * password-only re-login UI survives a page reload or browser restart.
   * The key is listed in {@link PRESERVED_ON_LOGOUT} so it is not swept by
   * the logout localStorage pass.
   */
  private persistReauthContext(): void {
    if (!this._lastKnownUsername) return;
    try {
      localStorage.setItem(
        REAUTH_CONTEXT_KEY,
        JSON.stringify({ email: this._lastKnownUsername, entityId: this._lastKnownEntityId }),
      );
    } catch { /* storage unavailable */ }
  }

  /**
   * Reads the persisted reauth context written by the previous page session
   * and restores the in-memory fields + `sessionExpiredNotice` flag so
   * `LoginComponent` can enter password-only re-login mode after a page reload.
   *
   * @remarks
   * Called once at the start of {@link restoreStoredToken}. If the stored
   * token turns out to be valid (or a silent refresh succeeds), the context
   * is discarded via {@link clearReauthContext}.
   */
  private restoreReauthContext(): void {
    try {
      const raw = localStorage.getItem(REAUTH_CONTEXT_KEY);
      if (!raw) return;
      const ctx = JSON.parse(raw) as { email?: unknown; entityId?: unknown };
      if (typeof ctx.email !== 'string') return;
      this._lastKnownUsername = ctx.email;
      this._lastKnownEntityId = typeof ctx.entityId === 'number' ? ctx.entityId : null;
      this.sessionExpiredNotice = true;
    } catch { /* corrupt or unavailable */ }
  }

  /**
   * Clears the in-memory reauth context and removes the persisted localStorage
   * entry. Called when the session is successfully restored (valid token or
   * successful silent refresh) or when the user completes a new login.
   */
  private clearReauthContext(): void {
    this._lastKnownUsername = null;
    this._lastKnownEntityId = null;
    this.sessionExpiredNotice = false;
    try { localStorage.removeItem(REAUTH_CONTEXT_KEY); } catch { /* unavailable */ }
  }

  /**
   * Builds the `HttpHeaders` required by `POST /auth/token`.
   *
   * @param username - Username for the Basic Auth header.
   * @param password - Password for the Basic Auth header.
   * @returns Headers including Basic Auth, Content-Type, and all device fields.
   */
  private async buildLoginHeaders(username: string, password: string): Promise<HttpHeaders> {
    const [deviceId, deviceType, appVersion, coords] = await Promise.all([
      this.getDeviceId(),
      this.getDeviceType(),
      this.getAppVersion(),
      this.getCoordinates(),
    ]);

    return new HttpHeaders({
      Authorization: `Basic ${btoa(`${username}:${password}`)}`,
      'Content-Type': 'application/x-www-form-urlencoded',
      'Device-ID': deviceId,
      'Device-Type': deviceType,
      'Device-Latitude': coords.latitude,
      'Device-Longitude': coords.longitude,
      'App-Version': appVersion,
    });
  }

  /**
   * Returns the unique device identifier, falling back to a random UUID
   * on platforms where the plugin is unavailable.
   */
  private async getDeviceId(): Promise<string> {
    try {
      const info = await Device.getId();
      return info.identifier;
    } catch {
      return crypto.randomUUID();
    }
  }

  /**
   * Returns the platform string expected by the API (`"iOS"`, `"Android"`,
   * or `"Web"`).
   */
  private async getDeviceType(): Promise<string> {
    try {
      const info = await Device.getInfo();
      const map: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web' };
      return map[info.platform] ?? 'Web';
    } catch {
      return 'Web';
    }
  }

  /**
   * Returns the installed app version string, falling back to `"1.0.0"`.
   */
  /**
   * Returns the app version string from Capacitor or package.json.
   * Formats: `{versionName}-{versionCode}` on native, `{version}` on web.
   */
  private async getAppVersion(): Promise<string> {
    try {
      const info = await App.getInfo();
      return `${info.version}-${info.build}`;
    } catch {
      // Web/browser fallback to package.json version
      return '1.0.0';
    }
  }

  /**
   * Returns the device's current latitude and longitude as strings,
   * falling back to `"0"` / `"0"` when Geolocation permission is denied,
   * the plugin is unavailable, or the iOS CLLocationManager RunLoop hang
   * causes the native `timeout` option to silently fail to fire.
   *
   * `Promise.race` provides a JS-level safety net at {@link GEO_JS_TIMEOUT_MS}
   * so the login request is never blocked indefinitely on a hung native call.
   */
  private async getCoordinates(): Promise<{ latitude: string; longitude: string }> {
    try {
      const position = await Promise.race([
        Geolocation.getCurrentPosition({ timeout: 5000 }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('geolocation timeout')), GEO_JS_TIMEOUT_MS),
        ),
      ]);
      return {
        latitude: position.coords.latitude.toFixed(6),
        longitude: position.coords.longitude.toFixed(6),
      };
    } catch {
      return { latitude: '0', longitude: '0' };
    }
  }
}
